import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  CircularProgress,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  FormControlLabel,
  InputAdornment,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import WarningIcon from "@material-ui/icons/Warning";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { CustomDialog, Toaster } from "@/components";
import UserContext from "@/contexts/UserContext";
import {
  createDraftService,
  getCommerceDgiiInfoService,
  getDraftErrorMessage,
  getValidateIdentificationErrorMessage,
  validateIdentificationService,
} from "@/services/assistedAffiliation";
import {
  accountTypeOptions,
  COMMERCIAL_NAME_REQUIRED_MESSAGE,
  CREATE_COMMERCE_CONFIRMATION_MODAL,
  connectivityOptions,
  DRAFT_SAVE_ERROR_MESSAGE,
  DRAFT_SAVE_SUCCESS_MESSAGE,
  buildCreationStatusOptions,
  initialAssistedAffiliationForm,
  initialAssistedAffiliationFiles,
  initialLegalDocumentFiles,
  legalDocuments,
  monthlySalesOptions,
  nationalityOptions,
  requiredAssistedAffiliationFields,
  yesNoOptions,
} from "../constants";
import {
  AlertState,
  AssistedAffiliationFiles,
  AssistedAffiliationFieldName,
  AssistedAffiliationForm,
  LegalDocumentFileName,
  LegalDocumentFiles,
  RncValidationFeedback,
  SelectOption,
  SingleAssistedAffiliationFileName,
} from "../types";
import AffiliationToggleList from "./AffiliationToggleList";
import FileUploadField from "./FileUploadField";
import FormSection from "./FormSection";
import LegalDocumentsUpload from "./LegalDocumentsUpload";
import RequestActionToolbar from "./RequestActionToolbar";
import RequestSummary from "./RequestSummary";
import RncValidationFeedbackCard from "./RncValidationFeedbackCard";
import StatusChangeConfirmationModal from "./StatusChangeConfirmationModal";
import useAssistedAffiliationStyles from "../styles";
import { formToDraftPayload } from "../draftMappers";
import { useAssistedAffiliationCatalogs } from "../hooks/useAssistedAffiliationCatalogs";
import {
  appendStatusChangeComment,
  formatStatusChangeComment,
} from "../statusCommentFormatter";
import {
  CommerceCreationGuard,
  createCommerceOnce,
} from "./commerceCreationGuard";
import { addCommercialNameToBilletFeedback } from "../rncValidationFeedback";

/**
 * Lo que solo existe al editar una solicitud ya creada. Va agrupado para que el tipo no admita
 * combinaciones imposibles, como un formulario poblado que no puede guardar.
 */
type AssistedAffiliationEditMode = {
  /** Siembra el formulario al montar; a partir de ahí manda lo que el usuario escribe. */
  defaultForm: AssistedAffiliationForm;
  /** Fecha ISO que asigna el servicio. */
  lastUpdatedAt: string;
  isSaving: boolean;
  onSave: (form: AssistedAffiliationForm) => Promise<void>;
};

type AssistedAffiliationManualRequestProps = {
  onBack: () => void;
  /** Cierra el formulario y permite que la pantalla padre refresque la bandeja tras crear. */
  onCreated?: () => void;
  /** Ausente = alta de una solicitud nueva. */
  edit?: AssistedAffiliationEditMode;
};

type FieldErrors = Partial<Record<AssistedAffiliationFieldName, string>>;
type FileErrors = Partial<Record<SingleAssistedAffiliationFileName, string>>;
type TouchedFields = Partial<Record<AssistedAffiliationFieldName, boolean>>;
type TouchedFiles = Partial<Record<SingleAssistedAffiliationFileName, boolean>>;
type LegalDocumentFileErrors = Partial<Record<LegalDocumentFileName, string>>;

const REPRESENTATIVE_FILE_MAX_SIZE = 25 * 1024 * 1024;
const REPRESENTATIVE_DOCUMENT_TYPES = ["image/jpeg", "application/pdf"];
const LEGAL_DOCUMENT_MAX_SIZE = 25 * 1024 * 1024;
const LEGAL_DOCUMENT_TYPES = ["image/jpeg", "application/pdf"];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NUMERIC_PATTERN = /^\d+$/;
const PASSPORT_PATTERN = /^[a-zA-Z0-9]+$/;
const ALPHANUMERIC_TEXT_PATTERN = /^[a-zA-ZÀ-ÿ0-9\s.,&'/-]+$/;

const getRepresentativeFileError = (file: File | null, required: boolean) => {
  if (required && !file) {
    return "Este archivo es requerido.";
  }

  if (!file) {
    return "";
  }

  if (!REPRESENTATIVE_DOCUMENT_TYPES.includes(file.type)) {
    return "Formato permitido: JPG o PDF.";
  }

  if (file.size > REPRESENTATIVE_FILE_MAX_SIZE) {
    return "El archivo no debe superar 25MB.";
  }

  return "";
};

const getLegalDocumentFileError = (file: File) => {
  if (!LEGAL_DOCUMENT_TYPES.includes(file.type)) {
    return "Formato permitido: JPG o PDF.";
  }

  if (file.size > LEGAL_DOCUMENT_MAX_SIZE) {
    return "El archivo no debe superar 25MB.";
  }

  return "";
};

const AssistedAffiliationManualRequest = ({
  onBack,
  onCreated,
  edit,
}: AssistedAffiliationManualRequestProps) => {
  const classes = useAssistedAffiliationStyles();
  const { user } = useContext(UserContext);
  const isEditing = Boolean(edit);
  const [form, setForm] = useState<AssistedAffiliationForm>(
    edit?.defaultForm ?? initialAssistedAffiliationForm
  );
  const [files, setFiles] = useState<AssistedAffiliationFiles>(
    initialAssistedAffiliationFiles
  );
  const [legalDocumentFiles, setLegalDocumentFiles] = useState<LegalDocumentFiles>(
    initialLegalDocumentFiles
  );
  const [legalDocumentFileErrors, setLegalDocumentFileErrors] =
    useState<LegalDocumentFileErrors>({});
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});
  const [touchedFiles, setTouchedFiles] = useState<TouchedFiles>({});
  const [hasSubmittedForm, setHasSubmittedForm] = useState(false);
  const [rncValidationExecuted, setRncValidationExecuted] = useState(false);
  const [rncValidationFeedback, setRncValidationFeedback] =
    useState<RncValidationFeedback | null>(null);
  const [isLegalNameManuallyEditable, setIsLegalNameManuallyEditable] =
    useState(Boolean(edit && !edit.defaultForm.legalName.trim()));
  const [isValidatingIdentification, setIsValidatingIdentification] =
    useState(false);
  const [pendingCreationStatus, setPendingCreationStatus] = useState("");
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    type: "info",
    text: "",
  });
  const [isCreatingCommerce, setIsCreatingCommerce] = useState(false);
  const [hasCreatedCommerce, setHasCreatedCommerce] = useState(false);
  const [isCreateConfirmationOpen, setIsCreateConfirmationOpen] = useState(false);
  const commerceCreationGuardRef = useRef<CommerceCreationGuard>({
    inFlight: false,
    completed: false,
  });
  const {
    catalogs,
    catalogError,
    clearCatalogError,
    loading: catalogLoading,
    loadMunicipalities,
    loadNeighborhoods,
    loadSectors,
  } = useAssistedAffiliationCatalogs();

  useEffect(() => {
    if (!catalogError) return;

    setAlert({
      show: true,
      type: "error",
      text: catalogError,
    });
    clearCatalogError();
  }, [catalogError, clearCatalogError]);

  const requiredFields = useMemo<AssistedAffiliationFieldName[]>(
    () => [
      ...requiredAssistedAffiliationFields,
      ...(form.isForeignRepresentative
        ? (["nationality", "passportNumber"] as AssistedAffiliationFieldName[])
        : (["identityNumber"] as AssistedAffiliationFieldName[])),
    ],
    [form.isForeignRepresentative]
  );

  const missingRequiredFields = useMemo(
    () =>
      requiredFields.filter((field) => {
        const value = form[field];

        if (typeof value === "boolean") {
          return !value;
        }

        return !String(value).trim();
      }),
    [form, requiredFields]
  );
  const missingRequiredFiles = useMemo<SingleAssistedAffiliationFileName[]>(
    () => {
      const representativeFiles: SingleAssistedAffiliationFileName[] =
        form.isForeignRepresentative
          ? ["passportPhoto"]
          : ["frontIdentification", "backIdentification"];
      const requiredFiles: SingleAssistedAffiliationFileName[] = [
        ...representativeFiles,
        "accountEvidence",
      ];

      return requiredFiles.filter((field) => !files[field]);
    },
    [files, form.isForeignRepresentative]
  );
  const missingRequiredLegalDocuments = useMemo(
    () =>
      legalDocuments.filter(
        (document) =>
          document.required && legalDocumentFiles[document.field].length === 0
      ),
    [legalDocumentFiles]
  );
  const hasMissingRequiredData =
    missingRequiredFields.length > 0 ||
    missingRequiredFiles.length > 0 ||
    missingRequiredLegalDocuments.length > 0;
  const visibleLegalDocumentFileErrors = useMemo<LegalDocumentFileErrors>(() => {
    if (!hasSubmittedForm) return legalDocumentFileErrors;

    return legalDocuments.reduce<LegalDocumentFileErrors>((errors, document) => {
      if (
        document.required &&
        legalDocumentFiles[document.field].length === 0 &&
        !errors[document.field]
      ) {
        errors[document.field] = "Este archivo es requerido.";
      }

      return errors;
    }, { ...legalDocumentFileErrors });
  }, [hasSubmittedForm, legalDocumentFileErrors, legalDocumentFiles]);
  const isRncValidationSuccessful = rncValidationFeedback?.tone === "success";
  const hasBlockingRncValidation = Boolean(
    rncValidationFeedback && !isRncValidationSuccessful
  );

  const representativeFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const names = form.representativeNames.trim();
    const lastNames = form.representativeLastNames.trim();
    const email = form.representativeEmail.trim();
    const phone = form.representativePhone.trim();
    const identity = form.identityNumber.trim();
    const passport = form.passportNumber.trim();

    if (!names) errors.representativeNames = "Este campo es requerido.";
    if (!lastNames) errors.representativeLastNames = "Este campo es requerido.";

    if (!email) {
      errors.representativeEmail = "Este campo es requerido.";
    } else if (!EMAIL_PATTERN.test(email)) {
      errors.representativeEmail = "Ingresa un correo electrónico válido.";
    }

    if (!phone) {
      errors.representativePhone = "Este campo es requerido.";
    } else if (!NUMERIC_PATTERN.test(phone)) {
      errors.representativePhone = "Ingresa solo números.";
    } else if (phone.length < 10 || phone.length > 12) {
      errors.representativePhone = "El celular debe tener entre 10 y 12 dígitos.";
    }

    if (form.isForeignRepresentative) {
      if (!form.nationality.trim()) {
        errors.nationality = "Este campo es requerido.";
      }

      if (!passport) {
        errors.passportNumber = "Este campo es requerido.";
      } else if (!PASSPORT_PATTERN.test(passport)) {
        errors.passportNumber = "Ingresa solo letras y números.";
      } else if (passport.length > 9) {
        errors.passportNumber = "El pasaporte no puede superar 9 caracteres.";
      }
    } else if (!identity) {
      errors.identityNumber = "Este campo es requerido.";
    } else if (!NUMERIC_PATTERN.test(identity)) {
      errors.identityNumber = "Ingresa solo números.";
    } else if (identity.length > 11) {
      errors.identityNumber = "La cédula no puede superar 11 dígitos.";
    }

    return errors;
  }, [form]);

  const representativeFileErrors = useMemo<FileErrors>(() => {
    const errors: FileErrors = {};

    if (form.isForeignRepresentative) {
      const passportPhotoError = getRepresentativeFileError(files.passportPhoto, true);
      if (passportPhotoError) errors.passportPhoto = passportPhotoError;
      return errors;
    }

    const frontError = getRepresentativeFileError(files.frontIdentification, true);
    const backError = getRepresentativeFileError(files.backIdentification, true);

    if (frontError) errors.frontIdentification = frontError;
    if (backError) errors.backIdentification = backError;

    return errors;
  }, [files, form.isForeignRepresentative]);

  const hasRepresentativeErrors =
    Object.keys(representativeFieldErrors).length > 0 ||
    Object.keys(representativeFileErrors).length > 0;

  const commerceFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const documentNumber = form.documentNumber.trim();
    const legalName = form.legalName.trim();
    const commercialName = form.commercialName.trim();

    if (!documentNumber) {
      errors.documentNumber = "Este campo es requerido.";
    } else if (!NUMERIC_PATTERN.test(documentNumber)) {
      errors.documentNumber = "Ingresa solo números.";
    } else if (documentNumber.length < 9 || documentNumber.length > 11) {
      errors.documentNumber = "El RNC/Cédula debe tener entre 9 y 11 dígitos.";
    }

    if (!commercialName) {
      errors.commercialName = "Este campo es requerido.";
    } else if (!ALPHANUMERIC_TEXT_PATTERN.test(commercialName)) {
      errors.commercialName = "Ingresa solo caracteres alfanuméricos.";
    }

    if (!legalName) errors.legalName = "Este campo es requerido.";
    if (!form.monthlySales.trim()) {
      errors.monthlySales = "Este campo es requerido.";
    }
    if (!String(form.businessType).trim()) {
      errors.businessType = "Este campo es requerido.";
    }

    return errors;
  }, [
    form.businessType,
    form.commercialName,
    form.documentNumber,
    form.legalName,
    form.monthlySales,
  ]);

  const hasCommerceErrors = Object.keys(commerceFieldErrors).length > 0;

  const addressFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const streetAndNumber = form.streetAndNumber.trim();
    const reference = form.reference.trim();

    if (!form.province.trim()) errors.province = "Este campo es requerido.";
    if (!form.municipality.trim()) errors.municipality = "Este campo es requerido.";
    if (!form.sector.trim()) errors.sector = "Este campo es requerido.";
    if (!form.neighborhood.trim()) errors.neighborhood = "Este campo es requerido.";

    if (!streetAndNumber) {
      errors.streetAndNumber = "Este campo es requerido.";
    } else if (!ALPHANUMERIC_TEXT_PATTERN.test(streetAndNumber)) {
      errors.streetAndNumber = "Ingresa solo caracteres alfanuméricos.";
    } else if (streetAndNumber.length > 100) {
      errors.streetAndNumber = "La calle y número no puede superar 100 caracteres.";
    }

    if (reference.length > 100) {
      errors.reference = "La referencia no puede superar 100 caracteres.";
    }

    return errors;
  }, [
    form.municipality,
    form.neighborhood,
    form.province,
    form.reference,
    form.sector,
    form.streetAndNumber,
  ]);

  const accountFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const accountNumber = form.accountNumber.trim();

    if (!form.bank.trim()) errors.bank = "Este campo es requerido.";
    if (!form.accountType.trim()) errors.accountType = "Este campo es requerido.";

    if (!accountNumber) {
      errors.accountNumber = "Este campo es requerido.";
    } else if (!NUMERIC_PATTERN.test(accountNumber)) {
      errors.accountNumber = "Ingresa solo números.";
    }

    return errors;
  }, [form.accountNumber, form.accountType, form.bank]);

  const accountFileErrors = useMemo<FileErrors>(() => {
    const errors: FileErrors = {};
    const accountEvidenceError = getRepresentativeFileError(files.accountEvidence, true);

    if (accountEvidenceError) {
      errors.accountEvidence = accountEvidenceError;
    }

    return errors;
  }, [files.accountEvidence]);

  const executiveFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const executiveEmail = form.executiveEmail.trim();
    const cashManagementEmail = form.cashManagementEmail.trim();

    if (executiveEmail && !EMAIL_PATTERN.test(executiveEmail)) {
      errors.executiveEmail = "Ingresa un correo electrónico válido.";
    }

    if (cashManagementEmail && !EMAIL_PATTERN.test(cashManagementEmail)) {
      errors.cashManagementEmail = "Ingresa un correo electrónico válido.";
    }

    return errors;
  }, [form.cashManagementEmail, form.executiveEmail]);

  const hasExecutiveErrors = Object.keys(executiveFieldErrors).length > 0;

  const billetSmartFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const rate = form.tapRate.trim();
    const quantity = form.tapQuantity.trim();

    if (rate && (Number.isNaN(Number(rate)) || Number(rate) <= 0)) {
      errors.tapRate = "Ingresa una tasa válida.";
    }

    if (quantity && (!NUMERIC_PATTERN.test(quantity) || Number(quantity) <= 0)) {
      errors.tapQuantity = "Ingresa una cantidad válida.";
    }

    return errors;
  }, [form.tapQuantity, form.tapRate]);

  const hasBilletSmartErrors = Object.keys(billetSmartFieldErrors).length > 0;
  const hasFormValidationErrors =
    hasRepresentativeErrors ||
    hasCommerceErrors ||
    Object.keys(addressFieldErrors).length > 0 ||
    Object.keys(accountFieldErrors).length > 0 ||
    Object.keys(accountFileErrors).length > 0 ||
    Object.values(legalDocumentFileErrors).some(Boolean) ||
    hasExecutiveErrors ||
    hasBilletSmartErrors;

  const touchFormFields = () => {
    const fieldsToTouch: AssistedAffiliationFieldName[] = [
      "representativeNames",
      "representativeLastNames",
      "representativeEmail",
      "representativePhone",
      "identityNumber",
      "passportNumber",
      "nationality",
      "documentNumber",
      "commercialName",
      "monthlySales",
      "businessType",
      "province",
      "municipality",
      "sector",
      "neighborhood",
      "streetAndNumber",
      "reference",
      "bank",
      "accountNumber",
      "accountType",
      "executiveCode",
      "executiveEmail",
      "cashManagementEmail",
      "commercialRepresentative",
      "creationStatus",
      "internalComments",
      "tapRate",
      "tapQuantity",
      "connectivityType",
      "tipEnabled",
    ];

    setTouchedFields((current) => ({
      ...current,
      ...fieldsToTouch.reduce<TouchedFields>(
        (accumulator, field) => ({ ...accumulator, [field]: true }),
        {}
      ),
    }));

    setTouchedFiles({
      frontIdentification: true,
      backIdentification: true,
      passportPhoto: true,
      accountEvidence: true,
    });
  };

  const updateField = (field: AssistedAffiliationFieldName) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;

    if (field === "documentNumber") {
      setRncValidationExecuted(false);
      setRncValidationFeedback(null);
      setIsLegalNameManuallyEditable(false);
      setForm((current) => ({
        ...current,
        documentNumber: value,
        legalName: "",
      }));
      return;
    }

    if (field === "province") {
      setForm((current) => ({
        ...current,
        province: value,
        municipality: "",
        sector: "",
        neighborhood: "",
      }));
      loadMunicipalities(value);
      return;
    }

    if (field === "municipality") {
      setForm((current) => ({
        ...current,
        municipality: value,
        sector: "",
        neighborhood: "",
      }));
      loadSectors(value);
      return;
    }

    if (field === "sector") {
      setForm((current) => ({
        ...current,
        sector: value,
        neighborhood: "",
      }));
      loadNeighborhoods(value);
      return;
    }

    if (field === "creationStatus") {
      if (value === form.creationStatus) return;
      setPendingCreationStatus(value);
      return;
    }

    setForm((current) => ({ ...current, [field]: value }));
  };

  const closeStatusChangeModal = () => {
    setPendingCreationStatus("");
  };

  const confirmStatusChange = (reason: string) => {
    const nextStatus = pendingCreationStatus;
    if (!nextStatus) return;

    const statusChangeComment = formatStatusChangeComment({
      changedAt: new Date().toISOString(),
      actorName: user?.username || "Usuario Backoffice",
      statusLabel: nextStatus,
      reason,
    });

    setForm((current) => ({
      ...current,
      creationStatus: nextStatus,
      internalComments: appendStatusChangeComment(
        current.internalComments,
        statusChangeComment
      ),
    }));
    setTouchedFields((current) => ({
      ...current,
      creationStatus: true,
      internalComments: true,
    }));
    closeStatusChangeModal();
  };

  const markFieldAsTouched = (field: AssistedAffiliationFieldName) => () => {
    setTouchedFields((current) => ({ ...current, [field]: true }));
  };

  const updateSwitch = (field: AssistedAffiliationFieldName) => (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setForm((current) => ({ ...current, [field]: event.target.checked }));
  };

  const updateFile = (field: SingleAssistedAffiliationFileName) => (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setFiles((current) => ({ ...current, [field]: selectedFile }));
    setTouchedFiles((current) => ({ ...current, [field]: true }));
  };

  const updateLegalDocumentFiles = (field: LegalDocumentFileName) => (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    const fileError = selectedFiles
      .map((file) => getLegalDocumentFileError(file))
      .find(Boolean);

    if (fileError) {
      setLegalDocumentFileErrors((current) => ({ ...current, [field]: fileError }));
      event.target.value = "";
      return;
    }

    setLegalDocumentFiles((current) => ({
      ...current,
      [field]: [...current[field], ...selectedFiles],
    }));
    setLegalDocumentFileErrors((current) => ({ ...current, [field]: "" }));
    event.target.value = "";
  };

  const removeFile = (field: SingleAssistedAffiliationFileName) => {
    setFiles((current) => ({ ...current, [field]: null }));
    setTouchedFiles((current) => ({ ...current, [field]: true }));
  };

  const removeLegalDocumentFile = (
    field: LegalDocumentFileName,
    fileIndex: number
  ) => {
    setLegalDocumentFiles((current) => ({
      ...current,
      [field]: current[field].filter((_, index) => index !== fileIndex),
    }));
  };

  const shouldShowFieldError = (field: AssistedAffiliationFieldName) =>
    Boolean(hasSubmittedForm || touchedFields[field]);

  const shouldShowFileError = (field: SingleAssistedAffiliationFileName) =>
    Boolean(hasSubmittedForm || touchedFiles[field]);

  const getFieldError = (field: AssistedAffiliationFieldName) =>
    shouldShowFieldError(field)
      ? representativeFieldErrors[field] ||
        commerceFieldErrors[field] ||
        addressFieldErrors[field] ||
        accountFieldErrors[field] ||
        executiveFieldErrors[field] ||
        billetSmartFieldErrors[field]
      : "";

  const getFileError = (field: SingleAssistedAffiliationFileName) =>
    shouldShowFileError(field)
      ? representativeFileErrors[field] || accountFileErrors[field]
      : "";

  const showPendingIntegrationAlert = (action: string) => {
    setAlert({
      show: true,
      type: "warning",
      text: `${action} requiere integración con el servicio de afiliación asistida.`,
    });
  };

  const renderFieldLabel = (label: string, required = false, htmlFor?: string) => (
    <InputLabel className={classes.fieldLabel} shrink htmlFor={htmlFor}>
      {label}
      {required ? <span className={classes.requiredMark}> *</span> : null}
    </InputLabel>
  );

  const renderLabeledTextField = (
    field: AssistedAffiliationFieldName,
    label: string,
    options?: {
      required?: boolean;
      disabled?: boolean;
      multiline?: boolean;
      placeholder?: string;
      type?: string;
      inputProps?: Record<string, unknown>;
      InputProps?: React.ComponentProps<typeof TextField>["InputProps"];
      onBlur?: () => void;
    }
  ) => {
    const error = getFieldError(field);
    const inputId = `assisted-affiliation-${field}`;

    return (
      <Box>
        {renderFieldLabel(label, options?.required, inputId)}
        <TextField
          id={inputId}
          fullWidth
          size="small"
          variant="outlined"
          value={form[field]}
          onChange={updateField(field)}
          onBlur={() => {
            markFieldAsTouched(field)();
            options?.onBlur?.();
          }}
          required={options?.required}
          disabled={options?.disabled}
          multiline={options?.multiline}
          minRows={options?.multiline ? 3 : undefined}
          placeholder={options?.placeholder}
          type={options?.type}
          inputProps={options?.inputProps}
          InputProps={options?.InputProps}
          error={Boolean(error)}
          helperText={error}
        />
      </Box>
    );
  };

  const renderLabeledSelect = (
    field: AssistedAffiliationFieldName,
    label: string,
    options: SelectOption[],
    required = false,
    disabled = false
  ) => {
    const error = getFieldError(field);
    const inputId = `assisted-affiliation-${field}`;

    return (
      <Box>
        {renderFieldLabel(label, required, inputId)}
        <TextField
          id={inputId}
          select
          fullWidth
          size="small"
          variant="outlined"
          value={form[field]}
          onChange={updateField(field)}
          onBlur={markFieldAsTouched(field)}
          required={required}
          disabled={disabled}
          error={Boolean(error)}
          helperText={error}
          SelectProps={{
            inputProps: { "aria-label": label },
          }}
        >
          <MenuItem value="">
            <em>Seleccione una opción</em>
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );
  };

  const renderFileControl = (
    field: SingleAssistedAffiliationFileName,
    title: string,
    required = false,
    accept = ".jpg,.jpeg,.pdf"
  ) => (
    <FileUploadField
      field={field}
      title={title}
      file={files[field]}
      error={getFileError(field)}
      required={required}
      accept={accept}
      onChange={updateFile}
      onRemove={removeFile}
    />
  );

  const renderLegalDocuments = () => (
    <LegalDocumentsUpload
      documents={legalDocuments}
      files={legalDocumentFiles}
      errors={visibleLegalDocumentFileErrors}
      onFilesChange={updateLegalDocumentFiles}
      onRemoveFile={removeLegalDocumentFile}
      onAddDocument={() => showPendingIntegrationAlert("Adjuntar documento")}
    />
  );

  const handleValidateIdentification = async () => {
    const documentNumber = form.documentNumber.trim();
    const isValidDocumentNumber =
      NUMERIC_PATTERN.test(documentNumber) &&
      documentNumber.length >= 9 &&
      documentNumber.length <= 11;

    if (!isValidDocumentNumber) return;

    setIsValidatingIdentification(true);

    try {
      const commerceDgiiInfo = await getCommerceDgiiInfoService(documentNumber);
      const legalName = commerceDgiiInfo?.name?.trim() || "";
      const commercialName =
        commerceDgiiInfo?.business_name?.trim() || legalName;
      const feedback = addCommercialNameToBilletFeedback(
        await validateIdentificationService(documentNumber),
        commercialName
      );

      setRncValidationFeedback(feedback);
      setRncValidationExecuted(true);
      setIsLegalNameManuallyEditable(feedback.tone === "success" && !legalName);
      setForm((current) => ({
        ...current,
        legalName: legalName || current.legalName,
        commercialName: current.commercialName || commercialName,
      }));

      if (feedback.tone === "success" && !legalName) {
        setAlert({
          show: true,
          type: "warning",
          text: "La identificación fue validada, pero no se pudo autocompletar la razón social desde DGII.",
        });
      }
    } catch (error) {
      setRncValidationExecuted(false);
      setRncValidationFeedback(null);
      setAlert({
        show: true,
        type: "error",
        text: getValidateIdentificationErrorMessage(error),
      });
    } finally {
      setIsValidatingIdentification(false);
    }
  };

  const renderRncValidationAdornment = () => {
    if (isValidatingIdentification) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={18} />
        </InputAdornment>
      );
    }

    if (!rncValidationExecuted || commerceFieldErrors.documentNumber) return null;

    if (rncValidationFeedback?.tone === "warning") {
      return (
        <InputAdornment position="end">
          <WarningIcon className={classes.rncWarningAdornment} />
        </InputAdornment>
      );
    }

    if (rncValidationFeedback?.tone === "error") {
      return (
        <InputAdornment position="end">
          <ErrorIcon className={classes.rncErrorAdornment} />
        </InputAdornment>
      );
    }

    return (
      <InputAdornment position="end">
        <CheckCircleIcon className={classes.rncAdornment} />
      </InputAdornment>
    );
  };

  const scrollToFirstInvalidSection = () => {
    const invalidSectionId =
      hasRepresentativeErrors
        ? "representative-section"
        : hasCommerceErrors
        ? "commerce-section"
        : Object.keys(addressFieldErrors).length > 0
        ? "address-section"
        : Object.keys(accountFieldErrors).length > 0 ||
          Object.keys(accountFileErrors).length > 0
        ? "account-section"
        : missingRequiredLegalDocuments.length > 0 ||
          Object.values(legalDocumentFileErrors).some(Boolean)
        ? "legal-documents-section"
        : hasExecutiveErrors
        ? "executive-section"
        : !form.billetSmart
        ? "affiliation-section"
        : hasBilletSmartErrors
        ? "billet-smart-section"
        : null;

    if (!invalidSectionId) return;

    window.requestAnimationFrame(() => {
      document
        .getElementById(invalidSectionId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // El formulario guarda identificadores y el servicio de creación pide además los nombres: se
  // resuelven contra el mismo catálogo que pinta cada select.
  const labelOf = (options: SelectOption[], value: string) =>
    options.find((option) => option.value === value)?.label ?? "";

  const catalogLabels = {
    provinceName: labelOf(catalogs.provinceOptions, form.province),
    municipalityName: labelOf(catalogs.municipalityOptions, form.municipality),
    sectorName: labelOf(catalogs.sectorOptions, form.sector),
    neighborhoodName: labelOf(catalogs.neighborhoodOptions, form.neighborhood),
    detailCategory: labelOf(catalogs.businessTypeOptions, form.businessType),
  };

  const handleSaveDraft = async () => {
    if (!edit) {
      showPendingIntegrationAlert("Guardar borrador");
      return;
    }

    // El nombre comercial es el único campo que el guardado exige: sin él no se manda nada.
    if (!form.commercialName.trim()) {
      setHasSubmittedForm(true);
      setAlert({
        show: true,
        type: "warning",
        text: COMMERCIAL_NAME_REQUIRED_MESSAGE,
      });
      document
        .getElementById("commerce-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    try {
      await edit.onSave(form);
      setAlert({
        show: true,
        type: "success",
        text: DRAFT_SAVE_SUCCESS_MESSAGE,
      });
    } catch (error) {
      setAlert({
        show: true,
        type: "error",
        text: getDraftErrorMessage(error, DRAFT_SAVE_ERROR_MESSAGE),
      });
    }
  };

  const handleCreateCommerce = () => {
    const guard = commerceCreationGuardRef.current;
    if (hasCreatedCommerce || guard.inFlight || guard.completed) return;

    setHasSubmittedForm(true);
    touchFormFields();

    if (hasMissingRequiredData) {
      setAlert({
        show: true,
        type: "warning",
        text: "Completa los campos requeridos antes de crear el comercio.",
      });
      scrollToFirstInvalidSection();
      return;
    }

    if (!isRncValidationSuccessful) {
      setAlert({
        show: true,
        type: hasBlockingRncValidation ? "error" : "warning",
        text: hasBlockingRncValidation
          ? "La identificación tiene restricciones que impiden crear el comercio."
          : "Ejecuta la validación de la identificación antes de crear el comercio.",
      });
      document
        .getElementById("commerce-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (hasFormValidationErrors) {
      setAlert({
        show: true,
        type: "warning",
        text: "Revisa los campos con validaciones antes de crear el comercio.",
      });
      scrollToFirstInvalidSection();
      return;
    }

    setIsCreateConfirmationOpen(true);
  };

  const handleConfirmCreateCommerce = async () => {
    if (
      hasCreatedCommerce ||
      isCreatingCommerce ||
      commerceCreationGuardRef.current.inFlight ||
      commerceCreationGuardRef.current.completed
    ) {
      return;
    }

    setIsCreatingCommerce(true);

    try {
      const created = await createCommerceOnce(
        commerceCreationGuardRef.current,
        () => createDraftService(formToDraftPayload(form, catalogLabels))
      );
      if (!created) return;
      // Una respuesta resuelta representa una creación aceptada por el servicio. El bloqueo se
      // conserva hasta salir del formulario para impedir repetir la misma solicitud.
      setHasCreatedCommerce(true);
      setIsCreateConfirmationOpen(false);
      if (onCreated) {
        onCreated();
      } else {
        setAlert({
          show: true,
          type: "success",
          text: "Solicitud creada exitosamente.",
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        type: "error",
        text: getDraftErrorMessage(error),
      });
    } finally {
      setIsCreatingCommerce(false);
    }
  };

  const renderAffiliationError = () =>
    hasSubmittedForm && !form.billetSmart ? (
      <Typography variant="caption" color="error">
        Billet Smart es requerido para crear el comercio.
      </Typography>
    ) : null;

  const summaryRows: Array<[string, string]> = [
    ["Registro #", "000125"],
    ["Fecha de creación", "22/06/2025 10:30 a. m."],
    ["Creado por", "Oficial Cash Management"],
    ["Estado actual", form.creationStatus || "-"],
    ["Última modificación", "22/06/2025 10:35 a. m."],
    ["Ejecutivo BHD", form.executiveCode || "Juan Pérez (JPEREZ)"],
    ["Representante comercial", form.commercialRepresentative || "María Gómez"],
  ];

  const sections = [
    {
      content: (
        <FormSection id="representative-section" number={1} title="Datos del representante">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderLabeledTextField("representativeNames", "Nombre(s)", {
                required: true,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledTextField("representativeLastNames", "Apellido(s)", {
                required: true,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledTextField("representativeEmail", "Correo electrónico", {
                required: true,
                type: "email",
              })}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderLabeledTextField("representativePhone", "Celular", {
                required: true,
                inputProps: { inputMode: "numeric", maxLength: 12 },
              })}
            </Grid>
            <Grid item xs={12} md={2}>
              {renderFieldLabel("¿Es extranjero?", true)}
              <RadioGroup
                row
                value={form.isForeignRepresentative ? "Sí" : "No"}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    isForeignRepresentative: event.target.value === "Sí",
                  }))
                }
              >
                {yesNoOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={(
                      <Radio
                        color="default"
                        icon={<RadioButtonUncheckedIcon className={classes.stepRadio} />}
                        checkedIcon={<RadioButtonCheckedIcon className={classes.stepRadioChecked} />}
                      />
                    )}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
              {!form.isForeignRepresentative && form.identityNumber ? (
                <Typography variant="caption" color="textSecondary">
                  Br: {form.identityNumber}
                </Typography>
              ) : null}
            </Grid>
            {!form.isForeignRepresentative ? (
              <>
                <Grid item xs={12} md={6}>
                  {renderLabeledTextField("identityNumber", "Número de cédula", {
                    required: true,
                    inputProps: { inputMode: "numeric", maxLength: 11 },
                  })}
                </Grid>
                <Grid item xs={12} md={6} />
                <Grid item xs={12} md={6}>
                  {renderFileControl(
                    "frontIdentification",
                    "Foto de la cédula frontal",
                    true,
                    ".jpg,.jpeg,.pdf"
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderFileControl(
                    "backIdentification",
                    "Foto de la cédula dorsal",
                    true,
                    ".jpg,.jpeg,.pdf"
                  )}
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Box className={classes.passportCard}>
                  <Box className={classes.passportHeader}>
                    <Typography variant="subtitle2">Datos del pasaporte</Typography>
                    <ExpandLessIcon fontSize="small" />
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {renderLabeledSelect(
                        "nationality",
                        "Código de país",
                        nationalityOptions,
                        true
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {renderLabeledTextField("passportNumber", "Número de pasaporte", {
                        required: true,
                        inputProps: { maxLength: 9 },
                      })}
                    </Grid>
                    <Grid item xs={12}>
                      {renderFileControl(
                        "passportPhoto",
                        "Foto o documento del pasaporte",
                        true,
                        ".jpg,.jpeg,.pdf"
                      )}
                      <Typography variant="caption" color="textSecondary">
                        Formatos permitidos: JPG, PDF. Máx. 25MB
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="commerce-section" number={2} title="Datos del comercio">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderLabeledTextField("documentNumber", "Número de identificación (RNC/Cédula)", {
                required: true,
                // Identifica la solicitud ante el servicio: una vez creada, ya no se cambia.
                disabled: isEditing,
                inputProps: { inputMode: "numeric", minLength: 9, maxLength: 11 },
                InputProps: {
                  endAdornment: renderRncValidationAdornment(),
                },
                onBlur: isEditing ? undefined : handleValidateIdentification,
              })}
            </Grid>
            {rncValidationExecuted &&
            !commerceFieldErrors.documentNumber &&
            rncValidationFeedback ? (
              <Grid item xs={12}>
                <RncValidationFeedbackCard feedback={rncValidationFeedback} />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              {renderFieldLabel("Nombre legal (Razón social)", true)}
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={form.legalName}
                onChange={updateField("legalName")}
                onBlur={markFieldAsTouched("legalName")}
                required
                disabled={!isLegalNameManuallyEditable}
                placeholder="Autocompletar con DGII"
                className={isLegalNameManuallyEditable ? undefined : classes.lockedInput}
                InputProps={
                  isLegalNameManuallyEditable
                    ? undefined
                    : {
                        endAdornment: (
                          <InputAdornment position="end">
                            <LockOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }
                }
                error={Boolean(getFieldError("legalName"))}
              />
              <Typography className={classes.fieldHelper}>
                {isLegalNameManuallyEditable
                  ? "DGII no devolvió la razón social. Complétala manualmente."
                  : "Este dato proviene de DGII y no puede ser modificado."}
              </Typography>
              {getFieldError("legalName") ? (
                <Typography variant="caption" color="error">
                  {getFieldError("legalName")}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("commercialName", "Nombre comercial", {
                inputProps: { maxLength: 100 },
                required: true,
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledSelect(
                "monthlySales",
                "Aproximado de ventas mensuales",
                monthlySalesOptions,
                true
              )}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledSelect(
                "businessType",
                "Tipo de negocio",
                catalogs.businessTypeOptions,
                true,
                catalogLoading.initial
              )}
            </Grid>
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="address-section" number={3} title="Datos de la dirección">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderLabeledSelect(
                "province",
                "Provincia",
                catalogs.provinceOptions,
                true,
                catalogLoading.initial
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledSelect(
                "municipality",
                "Municipio",
                catalogs.municipalityOptions,
                true,
                !form.province || catalogLoading.municipalities
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledSelect(
                "sector",
                "Sector",
                catalogs.sectorOptions,
                true,
                !form.municipality || catalogLoading.sectors
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledSelect(
                "neighborhood",
                "Paraje / Barrio",
                catalogs.neighborhoodOptions,
                true,
                !form.sector || catalogLoading.neighborhoods
              )}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("streetAndNumber", "Calle y número", {
                required: true,
                inputProps: { maxLength: 100 },
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("reference", "Referencia", {
                inputProps: { maxLength: 100 },
              })}
            </Grid>
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="account-section" number={4} title="Cuenta para recibir los fondos">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderLabeledSelect(
                "bank",
                "Banco",
                catalogs.bankOptions,
                true,
                catalogLoading.initial
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledTextField("accountNumber", "Número de cuenta", {
                required: true,
                inputProps: { inputMode: "numeric" },
              })}
            </Grid>
            <Grid item xs={12}>
              {renderFieldLabel("Tipo de cuenta", true)}
              <RadioGroup row value={form.accountType} onChange={updateField("accountType")}>
                {accountTypeOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={(
                      <Radio
                        color="default"
                        icon={<RadioButtonUncheckedIcon className={classes.stepRadio} />}
                        checkedIcon={<RadioButtonCheckedIcon className={classes.stepRadioChecked} />}
                      />
                    )}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
              {getFieldError("accountType") ? (
                <Typography variant="caption" color="error">
                  {getFieldError("accountType")}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              {renderFileControl("accountEvidence", "Evidencia de la cuenta bancaria", true)}
            </Grid>
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection
          id="legal-documents-section"
          number={5}
          title="Documentos legales"
          subtitle="Adjunte todos los documentos requeridos para la creación del comercio."
        >
          {renderLegalDocuments()}
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="executive-section" number={6} title="Datos del ejecutivo">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderLabeledTextField("executiveCode", "Código de colaborador Ejecutivo BHD", {
                inputProps: { maxLength: 50 },
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("executiveEmail", "Correo electrónico Ejecutivo BHD", {
                type: "email",
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField(
                "cashManagementEmail",
                "Correo electrónico Ejecutivo Cash Management",
                { type: "email" }
              )}
            </Grid>
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="commercial-management-section" number={7} title="Gestión comercial">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderLabeledTextField("commercialRepresentative", "Representante comercial", {
                required: true,
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledSelect(
                "creationStatus",
                "Estado de creación del comercio",
                buildCreationStatusOptions(form.creationStatus),
                true
              )}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("internalComments", "Comentarios internos", {
                multiline: true,
                placeholder: "Escribe un comentario o nota interna sobre esta solicitud...",
              })}
            </Grid>
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection
          id="affiliation-section"
          number={8}
          title="Tipo de afiliación"
          required
          subtitle="Activa los medios de cobro que deseas habilitar para este comercio."
        >
          <AffiliationToggleList form={form} onToggle={updateSwitch} />
          {renderAffiliationError()}
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="billet-smart-section" number={9} title="Configuración de Billet Smart">
          {form.billetSmart ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {renderLabeledTextField("tapRate", "Tasa (%)", {
                  type: "number",
                  inputProps: { step: "0.01" },
                })}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderLabeledTextField("tapQuantity", "Cantidad", {
                  type: "number",
                  inputProps: { min: 1, step: 1 },
                })}
              </Grid>
              <Grid item xs={12}>
                {renderFieldLabel("Tipo de conectividad")}
                <RadioGroup
                  row
                  className={classes.billetSmartRadioGroup}
                  value={form.connectivityType}
                  onChange={updateField("connectivityType")}
                >
                  {connectivityOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={(
                        <Radio
                          color="default"
                          icon={<RadioButtonUncheckedIcon className={classes.billetSmartRadio} />}
                          checkedIcon={<RadioButtonCheckedIcon className={classes.billetSmartRadioChecked} />}
                        />
                      )}
                      label={option.label}
                      classes={{ label: classes.billetSmartRadioLabel }}
                    />
                  ))}
                </RadioGroup>
                {getFieldError("connectivityType") ? (
                  <Typography variant="caption" color="error">
                    {getFieldError("connectivityType")}
                  </Typography>
                ) : null}
              </Grid>
              <Grid item xs={12}>
                {renderFieldLabel("Propina")}
                <RadioGroup
                  row
                  className={classes.billetSmartRadioGroup}
                  value={form.tipEnabled}
                  onChange={updateField("tipEnabled")}
                >
                  {yesNoOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={(
                        <Radio
                          color="default"
                          icon={<RadioButtonUncheckedIcon className={classes.billetSmartRadio} />}
                          checkedIcon={<RadioButtonCheckedIcon className={classes.billetSmartRadioChecked} />}
                        />
                      )}
                      label={option.label}
                      classes={{ label: classes.billetSmartRadioLabel }}
                    />
                  ))}
                </RadioGroup>
                {getFieldError("tipEnabled") ? (
                  <Typography variant="caption" color="error">
                    {getFieldError("tipEnabled")}
                  </Typography>
                ) : null}
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Activa Billet Smart en Tipo de afiliación para configurar este producto.
            </Typography>
          )}
        </FormSection>
      ),
    },
    {
      content: <RequestSummary id="summary-section" rows={summaryRows} />,
    },
  ];

  return (
    <>
      <Box className={classes.manualRequest}>
        <RequestActionToolbar
          status={form.creationStatus}
          lastUpdatedAt={edit?.lastUpdatedAt}
          createDisabled={
            hasMissingRequiredData ||
            !isRncValidationSuccessful ||
            isValidatingIdentification ||
            isCreatingCommerce ||
            hasCreatedCommerce
          }
          isCreating={isCreatingCommerce}
          saveLabel={isEditing ? "Guardar cambios" : undefined}
          isSaving={edit?.isSaving}
          onBack={onBack}
          onSaveDraft={handleSaveDraft}
          onCreateCommerce={handleCreateCommerce}
        />

        <Box className={classes.sectionsStack}>
          {sections.map((section, index) => (
            <Box key={index}>{section.content}</Box>
          ))}
        </Box>
      </Box>

      <Toaster
        show={alert.show}
        type={alert.type}
        text={alert.text}
        onClose={() => setAlert((current) => ({ ...current, show: false }))}
      />
      <CustomDialog
        variant="confirmation"
        icon={<HelpOutlineIcon />}
        isOpen={isCreateConfirmationOpen}
        title={CREATE_COMMERCE_CONFIRMATION_MODAL.title}
        text={CREATE_COMMERCE_CONFIRMATION_MODAL.description}
        acceptButtonLabel={CREATE_COMMERCE_CONFIRMATION_MODAL.confirmLabel}
        cancelButtonLabel={CREATE_COMMERCE_CONFIRMATION_MODAL.cancelLabel}
        actionsDisabled={isCreatingCommerce}
        okFunction={handleConfirmCreateCommerce}
        notOkFunction={() => {}}
        openCloseFx={setIsCreateConfirmationOpen}
      />
      <StatusChangeConfirmationModal
        isOpen={Boolean(pendingCreationStatus)}
        nextStatusLabel={pendingCreationStatus}
        onCancel={closeStatusChangeModal}
        onConfirm={confirmStatusChange}
      />
    </>
  );
};

export default AssistedAffiliationManualRequest;
